import axios from "axios";
import { UserStateInterface } from "../../../middleware/interfaces/user/user-state-interface";
import { ModelFragmentsInterface } from "../../../middleware/interfaces/file/model-fragments-interface";

/**
 * Stores the fragments buffer for a model that reached storage without one.
 *
 * Same three steps as a normal upload -- reserve, PUT straight to Wasabi,
 * confirm -- but only the derived buffer moves. The IFC is already in storage
 * and unchanged, so no file version is created.
 */

const IssueFragmentsUrl = async (fragmentsData: ModelFragmentsInterface) => {
  const url = "./php/file/model-fragments-issuer-proxy.php";

  // The buffer has no meaningful JSON form and must not be sent as metadata.
  const { modelFragmentsData, ...fragmentsMetaData } = fragmentsData as any;

  const response = await axios.post(url, fragmentsMetaData);
  return response.data;
};

const CompleteFragments = async (
  fragmentsData: ModelFragmentsInterface,
  modelID: number
) => {
  const url = "./php/file/model-fragments-completer-proxy.php";

  const response = await axios.post(url, {
    email: fragmentsData.email,
    projectID: fragmentsData.projectID,
    fileID: fragmentsData.fileID,
    // The row this client reserved. Two viewers opening the same unprepared
    // model each get their own, and confirming the wrong one would record a
    // size for a buffer that was never written.
    modelID: modelID,
  });

  return response.data;
};

export const ModelFragments = {
  Store: async (state: UserStateInterface) => {
    let serverResponse;

    if (state.data) {
      const fragmentsData: ModelFragmentsInterface = state.data;

      try {
        const issueResponse = await IssueFragmentsUrl(fragmentsData);

        // The issuer answers with "fragments" only once the row is reserved.
        // Anything else is a permission, quota or validation refusal that the
        // caller should surface unchanged.
        if (issueResponse.header !== "fragments") {
          return issueResponse;
        }

        // A bare client, not the shared axios default. Wasabi signs only the
        // host header, and anything an interceptor adds later would be
        // rejected alongside the query-string signature.
        await axios.create().put(
          issueResponse.data.url,
          fragmentsData.modelFragmentsData,
          { headers: { "Content-Type": "application/octet-stream" } }
        );

        serverResponse = await CompleteFragments(
          fragmentsData,
          issueResponse.data.modelID
        );
      } catch (error: any) {
        serverResponse = {
          status: "error",
          header: "error",
          message: error.message,
        };
      }
    } else {
      serverResponse = {
        status: "error",
        header: "error",
        message: "Something went wrong! Please contact DigitalCharcoal",
      };
    }

    return serverResponse;
  },
};
